import {
    type ComponentType,
    createContext,
    useContext,
    useEffect,
    type FC
} from "react";
import type {
    ProgressPartContentProps,
    ProgressContextType
} from "./type";
import {
    warnOnce
} from "../../utils/development";

export const ProgressContext = createContext<ProgressContextType | undefined>(undefined);

export const useProgressContext = ({
    part
}: {
    part: string;
}): ProgressContextType | undefined => {
    const context = useContext(ProgressContext);

    useEffect(() => {
        if(context) {
            return;
        }

        warnOnce({
            message: `Progress.${part}: must be rendered inside <Progress>.`
        });
    }, [
        context,
        part
    ]);

    return context;
};

export const withProgressContext = <P extends object>(
    part: string,
    Content: ComponentType<ProgressPartContentProps<P>>
): FC<P> => {
    const ProgressPart: FC<P> = (props) => {
        const context = useProgressContext({
            part
        });

        if(!context) {
            return null;
        }

        return <Content
            {...props}
            context={context}
        />;
    };

    ProgressPart.displayName = `Progress.${part}`;

    return ProgressPart;
};
